// Alternative power-up TOKEN designs (the pickup you drive over). We keep the exact
// same icon glyphs (white SVGs in /svg/powerup) and only play with everything AROUND
// them: fill, frame, shape, glow, gradient, animation. Pure canvas, drawn in PIXELS so
// the same function serves both the /pu gallery and the in-game renderer (which passes
// centerX*scale, centerY*scale, radius*scale). Switch in-game with ?pu=1..N.

// affects -> colour. "self" = a buff you'd want (green), "others" = a debuff you throw
// at rivals (red), "all" = hits everyone (blue). Design 1 keeps the legacy flat colours
// so it reads as "today"; the rest use a brighter palette.
import { readVariantParam } from "../shared/variant-param";
const LEGACY: Record<string, string> = { self: "#008000", others: "#FF0B0B", all: "#3453BC" };
const BRIGHT: Record<string, string> = { self: "#22c55e", others: "#ff3b52", all: "#4f7bff" };
const DARK: Record<string, string> = { self: "#0c2a17", others: "#340d13", all: "#111f42" };

const TAU = Math.PI * 2;

// --- glyph helpers ------------------------------------------------------------
// The SVGs are white shapes on transparent, so a colour-tinted copy lets us put the
// glyph on a light/pale token. Cache the tinted bitmap per (icon, colour).
const tintCache = new Map<string, HTMLCanvasElement>();
function tinted(image: any, color: string): HTMLCanvasElement | null {
  if (!image || !image.complete || !image.naturalWidth) return null;
  const key = image.src + "|" + color;
  let c = tintCache.get(key);
  if (!c) {
    c = document.createElement("canvas");
    c.width = image.naturalWidth; c.height = image.naturalHeight;
    const g = c.getContext("2d")!;
    g.drawImage(image, 0, 0);
    g.globalCompositeOperation = "source-in";
    g.fillStyle = color; g.fillRect(0, 0, c.width, c.height);
    tintCache.set(key, c);
  }
  return c;
}
function ready(image: any): boolean { return !!image && image.complete && image.naturalWidth > 0; }
// Chrome re-rasterizes an SVG image EVERY time its device-space draw size changes,
// and each raster is a synchronous layout + paint of the SVG's internal document on
// the main thread. The spawn/grab animations scale the token via ctx.scale() every
// frame, so drawing the Image directly re-rastered per frame per animating token:
// the game loop's only forced-layout source (measured ~35 layouts + 70 paints per
// second in a busy online round). Raster each glyph ONCE into a fixed-size offscreen
// canvas and draw from that; canvas-to-canvas draws are plain blits at any scale.
const GLYPH_RASTER = 192; // device px; in-game tokens draw well below this, so it only ever downscales
const rasterCache = new Map<string, HTMLCanvasElement>();
function rasterized(image: any): HTMLCanvasElement | null {
  if (!ready(image)) return null;
  let c = rasterCache.get(image.src);
  if (!c) {
    c = document.createElement("canvas");
    c.width = GLYPH_RASTER; c.height = GLYPH_RASTER;
    // Same geometry as the old direct draw: the glyph fills the square box.
    c.getContext("2d")!.drawImage(image, 0, 0, GLYPH_RASTER, GLYPH_RASTER);
    rasterCache.set(image.src, c);
  }
  return c;
}
// Draw the (white) glyph filling a box of half-size gr around (cx, cy).
function glyph(ctx: CanvasRenderingContext2D, image: any, cx: number, cy: number, gr: number): void {
  const c = rasterized(image);
  if (!c) return;
  // a 192px raster drawn at a dozen px: the default (low) smoothing samples it
  // too coarsely and the glyph goes soft and ragged; high filters it properly
  const q = ctx.imageSmoothingQuality;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(c, cx - gr, cy - gr, gr * 2, gr * 2);
  ctx.imageSmoothingQuality = q;
}
function glyphTinted(ctx: CanvasRenderingContext2D, image: any, cx: number, cy: number, gr: number, color: string): void {
  const t = tinted(image, color);
  if (t) ctx.drawImage(t, cx - gr, cy - gr, gr * 2, gr * 2);
}

// --- shape helpers ------------------------------------------------------------
function polyPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, sides: number, rot: number): void {
  ctx.beginPath();
  for (let i = 0; i < sides; i++) {
    const a = rot + (i / sides) * TAU;
    const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
  }
  ctx.closePath();
}
function roundRectPath(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, rad: number): void {
  const x = cx - r, y = cy - r, w = r * 2, h = r * 2;
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}
// hex colour -> rgba() string, so gradients can fade a colour to transparent.
function rgba(hex: string, a: number): string {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function arcStroke(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, a0: number, a1: number): void {
  ctx.beginPath(); ctx.arc(cx, cy, r, a0, a1); ctx.stroke();
}
// A 4-point star (twinkle).
function sparkle(ctx: CanvasRenderingContext2D, x: number, y: number, rad: number, color: string): void {
  ctx.fillStyle = color; ctx.beginPath();
  const spikes = 4, inner = rad * 0.34;
  for (let i = 0; i < spikes * 2; i++) {
    const rr = i % 2 ? inner : rad;
    const a = -Math.PI / 2 + (i * Math.PI) / spikes;
    ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath(); ctx.fill();
}

// --- coin base + shine helpers (designs 34..53 all sit on the static coin) -----
const fract = (x: number): number => x - Math.floor(x);
// The #33 coin: bright rim, dark face. Glyph is drawn separately by the caller.
// COIN_FACE is the face radius as a fraction of r; rim width = (1 - COIN_FACE) * r.
const COIN_FACE = 0.91; // thin rim (half the original 0.18r width)
const COIN_GLYPH = 0.78; // glyph radius fraction on the coin (bumped up a touch now the rim is thin)
function coinBase(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, bright: string, dark: string): void {
  ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
  ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r * COIN_FACE, 0, TAU); ctx.fill();
}
// True while rendering a token's STATIC base into the offscreen cache (see
// drawPowerUpTokenFast): the per-frame animated FX (glitter twinkles, the sheen
// band) draw nothing then, so the cached bitmap holds only the still parts.
let baseCachePass = false;

// A moving white gloss band clipped to the token (a diagonal "shiny wave" at angle).
// p in 0..1 slides the band across; innerR>0 clips to just the rim annulus.
function sheen(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, p: number, angle: number, halfWidth: number, alpha: number, innerR = 0): void {
  if (baseCachePass) return;
  ctx.save();
  ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU);
  if (innerR > 0) { ctx.arc(cx, cy, innerR, 0, TAU, true); ctx.clip("evenodd"); } else { ctx.clip(); }
  ctx.translate(cx, cy); ctx.rotate(angle);
  const x = -r * 1.4 + p * (r * 2.8);
  const lg = ctx.createLinearGradient(x - halfWidth, 0, x + halfWidth, 0);
  lg.addColorStop(0, rgba("#ffffff", 0)); lg.addColorStop(0.5, rgba("#ffffff", alpha)); lg.addColorStop(1, rgba("#ffffff", 0));
  ctx.fillStyle = lg; ctx.fillRect(-r * 1.6, -r * 1.6, r * 3.2, r * 3.2);
  ctx.restore();
}
// Scattered twinkling sparkles at deterministic (per-index) positions inside the face.
function glitter(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, t: number, phase: number, count: number): void {
  if (baseCachePass) return;
  for (let i = 0; i < count; i++) {
    const rx = fract(Math.sin(i * 12.9898 + phase) * 43758.5453) * 2 - 1;
    const ry = fract(Math.sin(i * 4.1414 + phase + 1.7) * 43758.5453) * 2 - 1;
    if (rx * rx + ry * ry > 1) continue;
    const ph = (t * 1.0 + i * 0.37 + phase) % 1, a = Math.sin(ph * Math.PI);
    if (a > 0.06) sparkle(ctx, cx + rx * r * 0.72, cy + ry * r * 0.72, r * 0.12 * a, rgba("#ffffff", a * 0.7));
  }
}
// The chosen #48 "innards": dark face + glyph + faint glitter + a slow diagonal gloss
// wave. Callers draw whatever RIM they like first; faceFrac sets where the face begins,
// i.e. it controls the rim width. Used by the rim-variant designs (54..63).
function coinFx(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, faceFrac: number, dark: string, image: any, t: number, phase: number): void {
  ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r * faceFrac, 0, TAU); ctx.fill();
  glyph(ctx, image, cx, cy, r * COIN_GLYPH);
  glitter(ctx, cx, cy, r * faceFrac, t, phase, 3);
  const period = 4.5, sweep = 1.2, cyc = (t + phase) % period;
  if (cyc < sweep) sheen(ctx, cx, cy, r, cyc / sweep, Math.PI / 4, r * 0.3, 0.22);
}

export const POWERUP_VARIANTS: { n: number; label: string }[] = [
  { n: 1, label: "Classic (today) — flat disc" },
  { n: 2, label: "Neon ring — dark core, glowing rim" },
  { n: 3, label: "Radial gradient + soft glow" },
  { n: 4, label: "Glass chip — bevel + top shine" },
  { n: 5, label: "Inverted — pale token, tinted glyph" },
  { n: 6, label: "Hexagon token" },
  { n: 7, label: "Rounded-square tile" },
  { n: 8, label: "Outline only — ring + tinted glyph" },
  { n: 9, label: "Pulsing halo" },
  { n: 10, label: "Diamond" },
  { n: 11, label: "Double ring" },
  { n: 12, label: "Coin — bright rim + bob" },
  // --- round two: research-driven ---
  { n: 13, label: "Glow orb — self-lit, soft halo" },
  { n: 14, label: "Glossy marble — specular + rim light" },
  { n: 15, label: "Neumorphic soft button" },
  { n: 16, label: "Claymorphic puff" },
  { n: 17, label: "Flat + floating drop shadow" },
  { n: 18, label: "Faceted gem (octagon)" },
  { n: 19, label: "Holographic sheen" },
  { n: 20, label: "Specular sweep" },
  { n: 21, label: "Rotating gear frame" },
  { n: 22, label: "Breathing bloom" },
  { n: 23, label: "Twinkle sparkles" },
  { n: 24, label: "Perforated ticket" },
  { n: 25, label: "Enamel pin — metal bezel" },
  { n: 26, label: "Marching-ants ring" },
  { n: 27, label: "HUD reticle brackets" },
  { n: 28, label: "Segmented arc meter" },
  { n: 29, label: "Plasma liquid core" },
  { n: 30, label: "Sunburst aura" },
  { n: 31, label: "Halftone comic chip" },
  { n: 32, label: "Reeded coin" },
  { n: 33, label: "Coin — static (no bob)" },
  // --- round three: coin (#33) base + discreet effects ---
  { n: 34, label: "Coin — diagonal shiny wave" },
  { n: 35, label: "Coin — faint glitter" },
  { n: 36, label: "Coin — gliding rim highlight" },
  { n: 37, label: "Coin — breathing rim glow" },
  { n: 38, label: "Coin — horizontal gloss wave" },
  { n: 39, label: "Coin — turning rim sheen" },
  { n: 40, label: "Coin — orbiting sparkles" },
  { n: 41, label: "Coin — breathing face shine" },
  { n: 42, label: "Coin — scanline glint" },
  { n: 43, label: "Coin — subtle iridescence" },
  { n: 44, label: "Coin — corner star wink" },
  { n: 45, label: "Coin — drifting dashed rim" },
  { n: 46, label: "Coin — soft outer bloom" },
  { n: 47, label: "Coin — criss-cross gleam" },
  { n: 48, label: "Coin — glitter + slow wave" },
  { n: 49, label: "Coin — rim tick (loading)" },
  { n: 50, label: "Coin — rim-only glint" },
  { n: 51, label: "Coin — gleam + corner spark" },
  { n: 52, label: "Coin — orbiting comet dot" },
  { n: 53, label: "Coin — centre ripple" },
  // --- round four: #48 look, different outer rims ---
  { n: 54, label: "Coin 48 — hairline rim" },
  { n: 55, label: "Coin 48 — thin rim" },
  { n: 56, label: "Coin 48 — medium rim" },
  { n: 57, label: "Coin 48 — bold rim" },
  { n: 58, label: "Coin 48 — double ring" },
  { n: 59, label: "Coin 48 — beveled rim" },
  { n: 60, label: "Coin 48 — top-lit rim" },
  { n: 61, label: "Coin 48 — glowing rim" },
  { n: 62, label: "Coin 48 — notched rim" },
  { n: 63, label: "Coin 48 — soft feathered rim" },
];

// cx, cy, r in PIXELS. `image` is the white glyph. `affects` is self|others|all.
// `t` is seconds (for animation). Draw a whole token.
export function drawPowerUpToken(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  image: any,
  affects: string,
  variant: number,
  t: number,
): void {
  const bright = BRIGHT[affects] || BRIGHT.all;
  const dark = DARK[affects] || DARK.all;
  const phase = (cx + cy) * 0.05; // per-token animation offset (stable, position-based)
  ctx.save();
  ctx.lineJoin = "round";

  switch (variant) {
    case 1: { // Classic: exactly today - flat legacy-colour disc + white glyph.
      ctx.fillStyle = LEGACY[affects] || LEGACY.all;
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r);
      break;
    }
    case 2: { // Neon ring: near-black core, bright glowing rim, white glyph.
      ctx.fillStyle = "rgba(8,10,14,0.92)";
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.shadowBlur = r * 0.7; ctx.shadowColor = bright;
      ctx.lineWidth = Math.max(1.5, r * 0.16); ctx.strokeStyle = bright;
      ctx.beginPath(); ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, TAU); ctx.stroke();
      ctx.shadowBlur = 0;
      glyph(ctx, image, cx, cy, r * 0.86);
      break;
    }
    case 3: { // Radial gradient disc + soft outer glow.
      const g = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.1, cx, cy, r);
      g.addColorStop(0, bright); g.addColorStop(1, dark);
      ctx.shadowBlur = r * 0.6; ctx.shadowColor = bright;
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.shadowBlur = 0;
      glyph(ctx, image, cx, cy, r * 0.92);
      break;
    }
    case 4: { // Glass chip: dark bevelled disc, bright thin ring, top-left shine.
      const g = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
      g.addColorStop(0, "rgba(60,70,90,0.95)"); g.addColorStop(1, "rgba(14,18,26,0.95)");
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.1); ctx.strokeStyle = bright;
      ctx.beginPath(); ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, TAU); ctx.stroke();
      ctx.globalAlpha = 0.5; ctx.strokeStyle = "#ffffff"; ctx.lineWidth = Math.max(1, r * 0.12);
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.7, Math.PI * 1.05, Math.PI * 1.6); ctx.stroke();
      ctx.globalAlpha = 1;
      glyph(ctx, image, cx, cy, r * 0.82);
      break;
    }
    case 5: { // Inverted: pale token, coloured ring, glyph TINTED to the affects colour.
      ctx.fillStyle = "#eef2f7"; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.lineWidth = Math.max(1.5, r * 0.16); ctx.strokeStyle = bright;
      ctx.beginPath(); ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, TAU); ctx.stroke();
      glyphTinted(ctx, image, cx, cy, r * 0.82, bright);
      break;
    }
    case 6: { // Hexagon token, flat bright fill + darker border.
      polyPath(ctx, cx, cy, r, 6, -Math.PI / 2); ctx.fillStyle = bright; ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.12); ctx.strokeStyle = dark; ctx.stroke();
      glyph(ctx, image, cx, cy, r * 0.72);
      break;
    }
    case 7: { // Rounded-square tile, vertical gradient, white glyph.
      const g = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
      g.addColorStop(0, bright); g.addColorStop(1, dark);
      roundRectPath(ctx, cx, cy, r * 0.92, r * 0.5); ctx.fillStyle = g; ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.1); ctx.strokeStyle = bright; ctx.stroke();
      glyph(ctx, image, cx, cy, r * 0.72);
      break;
    }
    case 8: { // Outline only: transparent core, thick coloured ring, tinted glyph.
      ctx.lineWidth = Math.max(1.5, r * 0.2); ctx.strokeStyle = bright;
      ctx.beginPath(); ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, TAU); ctx.stroke();
      glyphTinted(ctx, image, cx, cy, r * 0.8, bright);
      break;
    }
    case 9: { // Pulsing halo: flat disc + an expanding, fading ring.
      const p = (t * 0.9 + phase) % 1; // 0..1 loop
      ctx.globalAlpha = (1 - p) * 0.6;
      ctx.lineWidth = Math.max(1, r * 0.12); ctx.strokeStyle = bright;
      ctx.beginPath(); ctx.arc(cx, cy, r * (1 + p * 0.7), 0, TAU); ctx.stroke();
      ctx.globalAlpha = 1;
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.92);
      break;
    }
    case 10: { // Diamond (square rotated 45).
      polyPath(ctx, cx, cy, r, 4, -Math.PI / 2); ctx.fillStyle = bright; ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.12); ctx.strokeStyle = dark; ctx.stroke();
      glyph(ctx, image, cx, cy, r * 0.6);
      break;
    }
    case 11: { // Double ring: dark core + two concentric coloured rings.
      ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.strokeStyle = bright;
      ctx.lineWidth = Math.max(1, r * 0.1);
      ctx.beginPath(); ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, TAU); ctx.stroke();
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.66, 0, TAU); ctx.stroke();
      glyph(ctx, image, cx, cy, r * 0.5);
      break;
    }
    case 12: { // Coin - bright rim, dark face, gentle vertical bob.
      const bob = Math.sin(t * 2 + phase) * r * 0.12;
      const y = cy + bob;
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, y, r, 0, TAU); ctx.fill();
      ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, y, r * 0.82, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, y, r * 0.72);
      break;
    }

    // ===== round two: research-driven =====
    case 13: { // Glow orb: self-lit, saturated core melting into a soft halo.
      const halo = ctx.createRadialGradient(cx, cy, 0, cx, cy, r * 1.5);
      halo.addColorStop(0, rgba(bright, 0.9)); halo.addColorStop(0.45, rgba(bright, 0.5)); halo.addColorStop(1, rgba(bright, 0));
      ctx.fillStyle = halo; ctx.beginPath(); ctx.arc(cx, cy, r * 1.5, 0, TAU); ctx.fill();
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.82);
      break;
    }
    case 14: { // Glossy marble: off-centre light source, rim light, specular chip.
      const g = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.35, r * 0.1, cx, cy, r * 1.1);
      g.addColorStop(0, rgba("#ffffff", 0.85)); g.addColorStop(0.35, bright); g.addColorStop(1, dark);
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.1); ctx.strokeStyle = rgba("#ffffff", 0.45);
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.9, Math.PI * 1.05, Math.PI * 1.7); ctx.stroke();
      glyph(ctx, image, cx, cy, r * 0.8);
      ctx.fillStyle = rgba("#ffffff", 0.8); ctx.beginPath(); ctx.arc(cx - r * 0.38, cy - r * 0.4, r * 0.14, 0, TAU); ctx.fill();
      break;
    }
    case 15: { // Neumorphic: face extruded from the surface via paired soft shadows.
      ctx.save();
      ctx.shadowColor = rgba("#ffffff", 0.16); ctx.shadowBlur = r * 0.5; ctx.shadowOffsetX = -r * 0.22; ctx.shadowOffsetY = -r * 0.22;
      ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.shadowColor = rgba("#000000", 0.5); ctx.shadowOffsetX = r * 0.22; ctx.shadowOffsetY = r * 0.22;
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r * 0.88, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.82);
      break;
    }
    case 16: { // Claymorphic: puffy, slightly squashed, big soft shadow + top highlight.
      ctx.save();
      ctx.shadowColor = rgba("#000000", 0.45); ctx.shadowBlur = r * 0.7; ctx.shadowOffsetY = r * 0.18;
      ctx.fillStyle = bright; ctx.translate(cx, cy); ctx.scale(1, 0.92);
      ctx.beginPath(); ctx.arc(0, 0, r, 0, TAU); ctx.fill();
      ctx.restore();
      const hg = ctx.createRadialGradient(cx, cy - r * 0.4, r * 0.1, cx, cy - r * 0.4, r * 1.1);
      hg.addColorStop(0, rgba("#ffffff", 0.5)); hg.addColorStop(0.6, rgba("#ffffff", 0));
      ctx.fillStyle = hg; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.82);
      break;
    }
    case 17: { // Flat + drop shadow: pure solid colour floating off the arena.
      ctx.save();
      ctx.shadowColor = rgba("#000000", 0.55); ctx.shadowBlur = r * 0.5; ctx.shadowOffsetY = r * 0.22;
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.restore();
      glyph(ctx, image, cx, cy, r * 0.92);
      break;
    }
    case 18: { // Faceted gem: octagon cut into triangular facets of shifting shade.
      const sides = 8, rot = -Math.PI / 8, verts: [number, number][] = [];
      for (let i = 0; i < sides; i++) { const a = rot + (i / sides) * TAU; verts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]); }
      polyPath(ctx, cx, cy, r, sides, rot); ctx.fillStyle = dark; ctx.fill();
      for (let i = 0; i < sides; i++) {
        const [ax, ay] = verts[i], [bx, by] = verts[(i + 1) % sides];
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(ax, ay); ctx.lineTo(bx, by); ctx.closePath();
        ctx.fillStyle = i % 2 ? rgba(bright, 0.95) : rgba(bright, 0.6); ctx.fill();
      }
      ctx.fillStyle = rgba("#ffffff", 0.7);
      ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(verts[5][0], verts[5][1]); ctx.lineTo(verts[6][0], verts[6][1]); ctx.closePath(); ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.08); ctx.strokeStyle = dark; polyPath(ctx, cx, cy, r, sides, rot); ctx.stroke();
      glyph(ctx, image, cx, cy, r * 0.55);
      break;
    }
    case 19: { // Holographic sheen: an iridescent conic gradient (biased to the colour) that turns.
      ctx.save();
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.clip();
      const cg = (ctx as any).createConicGradient(t * 0.6 + phase, cx, cy);
      cg.addColorStop(0, bright); cg.addColorStop(0.25, rgba("#ffffff", 0.85)); cg.addColorStop(0.5, bright);
      cg.addColorStop(0.75, dark); cg.addColorStop(1, bright);
      ctx.fillStyle = cg; ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      ctx.restore();
      ctx.fillStyle = rgba(dark, 0.55); ctx.beginPath(); ctx.arc(cx, cy, r * 0.6, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.6);
      break;
    }
    case 20: { // Specular sweep: a diagonal gloss band glints across the solid token.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.save();
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.clip();
      const period = 3.2, p = ((t + phase) % period) / period;
      const sx = cx - r * 1.6 + p * (r * 3.2);
      ctx.translate(sx, cy); ctx.rotate(-0.5);
      const bw = r * 0.5;
      const lg = ctx.createLinearGradient(-bw, 0, bw, 0);
      lg.addColorStop(0, rgba("#ffffff", 0)); lg.addColorStop(0.5, rgba("#ffffff", 0.6)); lg.addColorStop(1, rgba("#ffffff", 0));
      ctx.fillStyle = lg; ctx.fillRect(-bw, -r * 2, bw * 2, r * 4);
      ctx.restore();
      glyph(ctx, image, cx, cy, r * 0.9);
      break;
    }
    case 21: { // Rotating gear frame: a slow-spinning cog around a still centre + glyph.
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(t * 0.6 + phase);
      const teeth = 8; ctx.beginPath();
      for (let i = 0; i < teeth * 2; i++) { const rr = i % 2 ? r * 0.78 : r; const a = (i / (teeth * 2)) * TAU; const fn = i === 0 ? "moveTo" : "lineTo"; (ctx as any)[fn](Math.cos(a) * rr, Math.sin(a) * rr); }
      ctx.closePath(); ctx.fillStyle = bright; ctx.fill();
      ctx.restore();
      ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r * 0.62, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.52);
      break;
    }
    case 22: { // Breathing bloom: outer glow (and a touch of scale) pulses like a heartbeat.
      const s = 0.5 + 0.5 * Math.sin(t * 2 + phase);
      const hr = r * (1.15 + 0.5 * s);
      const g = ctx.createRadialGradient(cx, cy, r * 0.5, cx, cy, hr);
      g.addColorStop(0, rgba(bright, 0.2 + 0.4 * s)); g.addColorStop(1, rgba(bright, 0));
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, hr, 0, TAU); ctx.fill();
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r * 0.9, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.9);
      break;
    }
    case 23: { // Twinkle sparkles: solid token with little stars winking at the rim.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.9);
      const spots: [number, number][] = [[-0.62, -0.7], [0.72, -0.38], [0.18, 0.76]];
      spots.forEach((sp, i) => {
        const ph = (t * 1.4 + phase + i * 0.6) % 1, a = Math.sin(ph * Math.PI);
        if (a > 0.02) sparkle(ctx, cx + sp[0] * r, cy + sp[1] * r, r * 0.34 * a, rgba("#ffffff", a));
      });
      break;
    }
    case 24: { // Perforated ticket: rounded-rect with punched notches + a dashed inner border.
      roundRectPath(ctx, cx, cy, r * 0.95, r * 0.35); ctx.fillStyle = bright; ctx.fill();
      ctx.save(); ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath(); ctx.arc(cx - r * 0.95, cy, r * 0.26, 0, TAU); ctx.arc(cx + r * 0.95, cy, r * 0.26, 0, TAU); ctx.fill();
      ctx.restore();
      ctx.setLineDash([r * 0.22, r * 0.15]); ctx.lineWidth = Math.max(1, r * 0.08); ctx.strokeStyle = rgba("#ffffff", 0.8);
      roundRectPath(ctx, cx, cy, r * 0.68, r * 0.22); ctx.stroke(); ctx.setLineDash([]);
      glyph(ctx, image, cx, cy, r * 0.58);
      break;
    }
    case 25: { // Enamel pin: metallic silver bezel around a glossy coloured enamel centre.
      const mg = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
      mg.addColorStop(0, "#f2f4f7"); mg.addColorStop(0.5, "#98a2b0"); mg.addColorStop(1, "#59616e");
      ctx.fillStyle = mg; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      const eg = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.1, cx, cy, r * 0.78);
      eg.addColorStop(0, rgba("#ffffff", 0.55)); eg.addColorStop(0.35, bright); eg.addColorStop(1, dark);
      ctx.fillStyle = eg; ctx.beginPath(); ctx.arc(cx, cy, r * 0.78, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.62);
      break;
    }
    case 26: { // Marching-ants ring: a scrolling dashed ring says "collectible".
      ctx.fillStyle = rgba(dark, 0.85); ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.setLineDash([r * 0.3, r * 0.22]); ctx.lineDashOffset = -(t * r * 1.5 + phase * 10);
      ctx.lineWidth = Math.max(1.5, r * 0.16); ctx.strokeStyle = bright;
      ctx.beginPath(); ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, TAU); ctx.stroke(); ctx.setLineDash([]);
      glyph(ctx, image, cx, cy, r * 0.78);
      break;
    }
    case 27: { // HUD reticle: solid core framed by four breathing corner brackets.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.82);
      const gap = r * (1.12 + 0.12 * Math.sin(t * 2 + phase)), len = r * 0.42;
      ctx.lineWidth = Math.max(1.5, r * 0.14); ctx.strokeStyle = bright; ctx.lineCap = "round";
      [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(([dx, dy]) => {
        const px = cx + dx * gap, py = cy + dy * gap;
        ctx.beginPath(); ctx.moveTo(px - dx * len, py); ctx.lineTo(px, py); ctx.lineTo(px, py - dy * len); ctx.stroke();
      });
      break;
    }
    case 28: { // Segmented arc meter: core ringed by a broken gauge of short arcs.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r * 0.68, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.6);
      const segs = 10, gp = 0.14, span = TAU / segs - gp;
      ctx.lineWidth = Math.max(1.5, r * 0.16); ctx.strokeStyle = bright;
      for (let i = 0; i < segs; i++) { const a0 = i * (TAU / segs); arcStroke(ctx, cx, cy, r * 0.92, a0, a0 + span); }
      break;
    }
    case 29: { // Plasma core: blobs of light roil inside the token like a lava lamp.
      ctx.save();
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.clip();
      ctx.fillStyle = dark; ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      ctx.globalCompositeOperation = "lighter";
      for (let k = 0; k < 3; k++) {
        const bx = cx + Math.cos(t * 0.8 + k * 2 + phase) * r * 0.5, by = cy + Math.sin(t * 1.1 + k * 2.5 + phase) * r * 0.5;
        const g = ctx.createRadialGradient(bx, by, 0, bx, by, r * 0.9);
        g.addColorStop(0, rgba(bright, 0.8)); g.addColorStop(1, rgba(bright, 0));
        ctx.fillStyle = g; ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      }
      ctx.restore();
      ctx.lineWidth = Math.max(1, r * 0.1); ctx.strokeStyle = rgba(bright, 0.9);
      ctx.beginPath(); ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, TAU); ctx.stroke();
      glyph(ctx, image, cx, cy, r * 0.78);
      break;
    }
    case 30: { // Sunburst aura: slow-turning rays of light behind an opaque core.
      ctx.save(); ctx.translate(cx, cy); ctx.rotate(t * 0.25 + phase); ctx.globalCompositeOperation = "lighter";
      const rays = 12;
      for (let i = 0; i < rays; i++) {
        const a = (i / rays) * TAU;
        ctx.beginPath(); ctx.moveTo(0, 0);
        ctx.lineTo(Math.cos(a - 0.07) * r * 1.5, Math.sin(a - 0.07) * r * 1.5);
        ctx.lineTo(Math.cos(a + 0.07) * r * 1.5, Math.sin(a + 0.07) * r * 1.5);
        ctx.closePath(); ctx.fillStyle = rgba(bright, i % 2 ? 0.1 : 0.22); ctx.fill();
      }
      ctx.restore();
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r * 0.85, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.82);
      break;
    }
    case 31: { // Halftone comic chip: coarse Ben-Day dots, bold outline, playful.
      ctx.save();
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.clip();
      ctx.fillStyle = bright; ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      ctx.fillStyle = rgba(dark, 0.9);
      const step = r * 0.42;
      for (let yy = cy - r; yy <= cy + r; yy += step) {
        const off = (Math.round((yy - cy) / step) % 2) * (step / 2);
        for (let xx = cx - r; xx <= cx + r; xx += step) {
          ctx.beginPath(); ctx.arc(xx + off, yy, r * 0.12, 0, TAU); ctx.fill();
        }
      }
      ctx.restore();
      ctx.lineWidth = Math.max(2, r * 0.16); ctx.strokeStyle = "#0b0f17";
      ctx.beginPath(); ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, TAU); ctx.stroke();
      glyph(ctx, image, cx, cy, r * 0.62);
      break;
    }
    case 32: { // Reeded coin: milled-edge ticks, raised colour face with a metallic sheen.
      const mg = ctx.createRadialGradient(cx - r * 0.3, cy - r * 0.3, r * 0.2, cx, cy, r);
      mg.addColorStop(0, rgba("#ffffff", 0.4)); mg.addColorStop(0.35, bright); mg.addColorStop(1, dark);
      ctx.fillStyle = mg; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.strokeStyle = rgba(dark, 0.9); ctx.lineWidth = Math.max(1, r * 0.06);
      const ticks = 24;
      for (let i = 0; i < ticks; i++) {
        const a = (i / ticks) * TAU;
        ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * r * 0.86, cy + Math.sin(a) * r * 0.86); ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r); ctx.stroke();
      }
      const fg = ctx.createRadialGradient(cx - r * 0.2, cy - r * 0.2, r * 0.1, cx, cy, r * 0.72);
      fg.addColorStop(0, rgba("#ffffff", 0.5)); fg.addColorStop(0.4, bright); fg.addColorStop(1, dark);
      ctx.fillStyle = fg; ctx.beginPath(); ctx.arc(cx, cy, r * 0.72, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * 0.6);
      break;
    }

    case 33: { // Coin (static): #12 without the bob - bright rim, dark face, glyph.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      break;
    }

    // ===== round three: the #33 coin as a base, with discreet/stylish effects =====
    case 34: { // Diagonal shiny wave: a soft gloss band drifts top-left -> bottom-right, with a pause.
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      const period = 3.4, sweep = 1.1, cyc = (t + phase) % period;
      if (cyc < sweep) sheen(ctx, cx, cy, r, cyc / sweep, Math.PI / 4, r * 0.32, 0.28);
      break;
    }
    case 35: { // Faint glitter drifting across the dark face.
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      glitter(ctx, cx, cy, r * 0.82, t, phase, 5);
      break;
    }
    case 36: { // A single soft highlight glides slowly around the rim.
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      const a = t * 0.9 + phase;
      ctx.save(); ctx.lineCap = "round"; ctx.strokeStyle = rgba("#ffffff", 0.5); ctx.lineWidth = r * 0.12;
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.91, a, a + 0.5); ctx.stroke(); ctx.restore();
      break;
    }
    case 37: { // Rim glow breathes gently.
      const s = 0.5 + 0.5 * Math.sin(t * 1.6 + phase);
      ctx.save(); ctx.shadowBlur = r * (0.12 + 0.28 * s); ctx.shadowColor = bright;
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill(); ctx.restore();
      ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      break;
    }
    case 38: { // Horizontal gloss wave (left -> right) with a pause.
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      const period = 3.4, sweep = 1.1, cyc = (t + phase) % period;
      if (cyc < sweep) sheen(ctx, cx, cy, r, cyc / sweep, 0, r * 0.3, 0.26);
      break;
    }
    case 39: { // Slow metallic sheen turning around the rim only.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.save();
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.arc(cx, cy, r * 0.82, 0, TAU, true); ctx.clip("evenodd");
      const cg = (ctx as any).createConicGradient(t * 0.5 + phase, cx, cy);
      cg.addColorStop(0, rgba("#ffffff", 0)); cg.addColorStop(0.12, rgba("#ffffff", 0.4)); cg.addColorStop(0.28, rgba("#ffffff", 0));
      cg.addColorStop(0.62, rgba("#ffffff", 0)); cg.addColorStop(0.78, rgba("#ffffff", 0.28)); cg.addColorStop(0.92, rgba("#ffffff", 0));
      ctx.fillStyle = cg; ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
      ctx.restore();
      ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      break;
    }
    case 40: { // Two small sparkles orbit slowly just inside the rim.
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      for (let i = 0; i < 2; i++) {
        const a = t * 0.8 + phase + i * Math.PI;
        const tw = 0.55 + 0.45 * Math.sin(t * 3 + i * 2);
        sparkle(ctx, cx + Math.cos(a) * r * 0.9, cy + Math.sin(a) * r * 0.9, r * 0.13 * tw, rgba("#ffffff", 0.7 * tw));
      }
      break;
    }
    case 41: { // A soft top-left shine on the face breathes in and out.
      coinBase(ctx, cx, cy, r, bright, dark);
      const s = 0.5 + 0.5 * Math.sin(t * 1.6 + phase);
      const g = ctx.createRadialGradient(cx - r * 0.22, cy - r * 0.22, r * 0.05, cx, cy, r * 0.82);
      g.addColorStop(0, rgba("#ffffff", 0.1 + 0.22 * s)); g.addColorStop(0.7, rgba("#ffffff", 0));
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.fill();
      glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      break;
    }
    case 42: { // A thin light line scans down the face now and then.
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      const period = 3.2, sweep = 1.2, cyc = (t + phase) % period;
      if (cyc < sweep) {
        const yy = cy - r * 0.82 + (cyc / sweep) * r * 1.64;
        ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.clip();
        const lg = ctx.createLinearGradient(0, yy - r * 0.22, 0, yy + r * 0.22);
        lg.addColorStop(0, rgba("#ffffff", 0)); lg.addColorStop(0.5, rgba("#ffffff", 0.3)); lg.addColorStop(1, rgba("#ffffff", 0));
        ctx.fillStyle = lg; ctx.fillRect(cx - r, yy - r * 0.22, r * 2, r * 0.44); ctx.restore();
      }
      break;
    }
    case 43: { // Subtle iridescence turning slowly across the face.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.fill();
      ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.clip();
      const cg = (ctx as any).createConicGradient(t * 0.4 + phase, cx, cy);
      cg.addColorStop(0, rgba(bright, 0)); cg.addColorStop(0.3, rgba(bright, 0.35)); cg.addColorStop(0.5, rgba("#ffffff", 0.18));
      cg.addColorStop(0.7, rgba(bright, 0.35)); cg.addColorStop(1, rgba(bright, 0));
      ctx.fillStyle = cg; ctx.fillRect(cx - r, cy - r, r * 2, r * 2); ctx.restore();
      glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      break;
    }
    case 44: { // A small star winks at the top-left, occasionally.
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      const period = 2.8, cyc = (t + phase) % period, a = cyc < 0.7 ? Math.sin((cyc / 0.7) * Math.PI) : 0;
      if (a > 0.05) sparkle(ctx, cx - r * 0.46, cy - r * 0.46, r * 0.3 * a, rgba("#ffffff", 0.85 * a));
      break;
    }
    case 45: { // A fine dashed ring drifts around the rim.
      coinBase(ctx, cx, cy, r, bright, dark);
      ctx.save(); ctx.setLineDash([r * 0.22, r * 0.16]); ctx.lineDashOffset = -(t * r * 0.8 + phase * 10);
      ctx.lineWidth = r * 0.08; ctx.strokeStyle = rgba("#ffffff", 0.4);
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.91, 0, TAU); ctx.stroke(); ctx.restore();
      glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      break;
    }
    case 46: { // A soft outer bloom breathes; coin stays still.
      const s = 0.5 + 0.5 * Math.sin(t * 1.5 + phase), hr = r * (1.08 + 0.28 * s);
      const g = ctx.createRadialGradient(cx, cy, r * 0.7, cx, cy, hr);
      g.addColorStop(0, rgba(bright, 0.1 + 0.22 * s)); g.addColorStop(1, rgba(bright, 0));
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, hr, 0, TAU); ctx.fill();
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      break;
    }
    case 47: { // Two crossing gloss waves, offset in time (a slow criss-cross gleam).
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      const period = 4.2, cyc = (t + phase) % period;
      if (cyc < 1.0) sheen(ctx, cx, cy, r, cyc / 1.0, Math.PI / 4, r * 0.3, 0.24);
      else if (cyc > 1.4 && cyc < 2.4) sheen(ctx, cx, cy, r, (cyc - 1.4) / 1.0, -Math.PI / 4, r * 0.3, 0.24);
      break;
    }
    case 48: { // Whisper glitter + a slow diagonal wave (combined, both faint).
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      glitter(ctx, cx, cy, r * 0.82, t, phase, 3);
      const period = 4.5, sweep = 1.2, cyc = (t + phase) % period;
      if (cyc < sweep) sheen(ctx, cx, cy, r, cyc / sweep, Math.PI / 4, r * 0.3, 0.22);
      break;
    }
    case 49: { // One rim segment lights up and travels around (a quiet loading tick).
      coinBase(ctx, cx, cy, r, bright, dark);
      const segs = 8, lit = Math.floor(t * 3 + phase) % segs;
      ctx.lineWidth = r * 0.12;
      for (let i = 0; i < segs; i++) {
        const a0 = i * (TAU / segs) + 0.06, a1 = (i + 1) * (TAU / segs) - 0.06;
        ctx.strokeStyle = i === lit ? rgba("#ffffff", 0.85) : rgba("#000000", 0.18);
        arcStroke(ctx, cx, cy, r * 0.91, a0, a1);
      }
      glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      break;
    }
    case 50: { // The gloss wave, but only glinting on the rim (matte face).
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      const period = 3.2, sweep = 1.1, cyc = (t + phase) % period;
      if (cyc < sweep) sheen(ctx, cx, cy, r, cyc / sweep, Math.PI / 4, r * 0.28, 0.5, r * 0.82);
      break;
    }
    case 51: { // A brief, soft full-token gleam every few seconds + a tiny corner spark.
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      const period = 3.6, cyc = (t + phase) % period, a = cyc < 0.5 ? Math.sin((cyc / 0.5) * Math.PI) : 0;
      if (a > 0.02) {
        ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.clip();
        ctx.fillStyle = rgba("#ffffff", 0.16 * a); ctx.fillRect(cx - r, cy - r, r * 2, r * 2); ctx.restore();
        sparkle(ctx, cx + r * 0.42, cy - r * 0.42, r * 0.22 * a, rgba("#ffffff", 0.8 * a));
      }
      break;
    }
    case 52: { // A tiny bright dot orbits inside the rim, trailing a short comet tail.
      coinBase(ctx, cx, cy, r, bright, dark); glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      const a = t * 1.4 + phase;
      for (let k = 4; k >= 0; k--) {
        const aa = a - k * 0.13;
        ctx.fillStyle = rgba("#ffffff", 0.55 * (1 - k / 5));
        ctx.beginPath(); ctx.arc(cx + Math.cos(aa) * r * 0.9, cy + Math.sin(aa) * r * 0.9, r * 0.09 * (1 - k / 6), 0, TAU); ctx.fill();
      }
      break;
    }
    // ===== round four: #48's look with different OUTER RIM treatments =====
    case 54: { // Hairline rim.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      coinFx(ctx, cx, cy, r, 0.95, dark, image, t, phase);
      break;
    }
    case 55: { // Thin rim.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      coinFx(ctx, cx, cy, r, 0.90, dark, image, t, phase);
      break;
    }
    case 56: { // Medium rim.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      coinFx(ctx, cx, cy, r, 0.84, dark, image, t, phase);
      break;
    }
    case 57: { // Bold rim.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      coinFx(ctx, cx, cy, r, 0.76, dark, image, t, phase);
      break;
    }
    case 58: { // Double ring: outer rim + a thin inner ring with a dark gap between.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.fillStyle = dark; ctx.beginPath(); ctx.arc(cx, cy, r * 0.9, 0, TAU); ctx.fill();
      ctx.lineWidth = Math.max(1, r * 0.06); ctx.strokeStyle = bright;
      ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.stroke();
      coinFx(ctx, cx, cy, r, 0.76, dark, image, t, phase);
      break;
    }
    case 59: { // Beveled rim: a glossy bright->white->dark radial gradient on the ring.
      const g = ctx.createRadialGradient(cx, cy, r * 0.82, cx, cy, r);
      g.addColorStop(0, rgba("#ffffff", 0.8)); g.addColorStop(0.45, bright); g.addColorStop(1, dark);
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      coinFx(ctx, cx, cy, r, 0.82, dark, image, t, phase);
      break;
    }
    case 60: { // Top-lit rim: a vertical gradient so the ring catches light up top.
      const g = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
      g.addColorStop(0, rgba("#ffffff", 0.65)); g.addColorStop(0.5, bright); g.addColorStop(1, dark);
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      coinFx(ctx, cx, cy, r, 0.86, dark, image, t, phase);
      break;
    }
    case 61: { // Glowing rim: the ring carries a soft neon bloom.
      ctx.save(); ctx.shadowBlur = r * 0.3; ctx.shadowColor = bright;
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill(); ctx.restore();
      coinFx(ctx, cx, cy, r, 0.88, dark, image, t, phase);
      break;
    }
    case 62: { // Notched rim: fine dark ticks milled into the ring.
      ctx.fillStyle = bright; ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
      ctx.strokeStyle = dark; ctx.lineWidth = Math.max(1, r * 0.05);
      const ticks = 16;
      for (let i = 0; i < ticks; i++) {
        const a = (i / ticks) * TAU;
        ctx.beginPath(); ctx.moveTo(cx + Math.cos(a) * r * 0.84, cy + Math.sin(a) * r * 0.84); ctx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r); ctx.stroke();
      }
      coinFx(ctx, cx, cy, r, 0.84, dark, image, t, phase);
      break;
    }
    case 63: { // Soft rim: the ring's outer edge feathers out (no hard border).
      const g = ctx.createRadialGradient(cx, cy, r * 0.82, cx, cy, r * 1.04);
      g.addColorStop(0, bright); g.addColorStop(0.72, bright); g.addColorStop(1, rgba(bright, 0));
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(cx, cy, r * 1.04, 0, TAU); ctx.fill();
      coinFx(ctx, cx, cy, r, 0.86, dark, image, t, phase);
      break;
    }

    default: { // 53: A faint ripple rings out from the centre across the face.
      if (variant === 53) {
        coinBase(ctx, cx, cy, r, bright, dark);
        ctx.save(); ctx.beginPath(); ctx.arc(cx, cy, r * 0.82, 0, TAU); ctx.clip();
        const p = ((t * 0.6 + phase) % 1);
        ctx.globalAlpha = (1 - p) * 0.4; ctx.lineWidth = r * 0.07; ctx.strokeStyle = "#ffffff";
        ctx.beginPath(); ctx.arc(cx, cy, r * 0.82 * p, 0, TAU); ctx.stroke(); ctx.restore();
        glyph(ctx, image, cx, cy, r * COIN_GLYPH);
      } else { // fall back to today's flat disc for any unknown variant.
        ctx.fillStyle = LEGACY[affects] || LEGACY.all;
        ctx.beginPath(); ctx.arc(cx, cy, r, 0, TAU); ctx.fill();
        glyph(ctx, image, cx, cy, r);
      }
      break;
    }
  }
  ctx.restore();
}

// --- cached in-game drawing ---------------------------------------------------
// drawPowerUpToken rebuilds every gradient and stroke of a token EVERY frame; with a
// handful of tokens on screen that was the single biggest per-frame JS cost (~2.4ms
// measured in a live round). The shipped designs (the coin family, 54-63) split
// cleanly into a STATIC base (rim + face + glyph, identical every frame) and tiny
// per-frame FX (glitter twinkles + the occasional sheen sweep). Render the base once
// into an offscreen canvas per (variant, colour, icon, radius) and blit it, then draw
// only the FX live. Other variants (the /pu gallery's experiments) keep the full draw.
const COIN_FACE_FRAC: Record<number, number> = { 54: 0.95, 55: 0.90, 56: 0.84, 57: 0.76, 58: 0.76, 59: 0.82, 60: 0.86, 61: 0.88, 62: 0.84, 63: 0.86 };
const TOKEN_BASE_PAD = 1.4; // glow rims (61) and soft rims (63) bleed past r; box half-size = r * PAD
const tokenBaseCache = new Map<string, HTMLCanvasElement>();

export function drawPowerUpTokenFast(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  image: any,
  affects: string,
  variant: number,
  t: number,
): void {
  const faceFrac = COIN_FACE_FRAC[variant];
  if (faceFrac === undefined || r <= 0 || !ready(image)) {
    // Not a cacheable design (or the glyph hasn't decoded yet - caching a glyph-less
    // base would freeze it that way): the plain full draw.
    drawPowerUpToken(ctx, cx, cy, r, image, affects, variant, t);
    return;
  }
  const rq = Math.max(1, Math.ceil(r)); // quantized cache radius (in-game r is constant per map anyway)
  const key = variant + "|" + affects + "|" + image.src + "|" + rq;
  let base = tokenBaseCache.get(key);
  if (!base) {
    const half = Math.ceil(rq * TOKEN_BASE_PAD);
    base = document.createElement("canvas");
    base.width = base.height = half * 2;
    baseCachePass = true;
    try {
      drawPowerUpToken(base.getContext("2d")!, half, half, rq, image, affects, variant, 0);
    } finally {
      baseCachePass = false;
    }
    tokenBaseCache.set(key, base);
  }
  const scale = r / rq;
  const half = base.width / 2;
  ctx.drawImage(base, cx - half * scale, cy - half * scale, base.width * scale, base.height * scale);
  // The per-frame FX the cached base skipped: same math as coinFx, on the live clock.
  ctx.save();
  const phase = (cx + cy) * 0.05;
  glitter(ctx, cx, cy, r * faceFrac, t, phase, 3);
  const period = 4.5, sweep = 1.2, cyc = (t + phase) % period;
  if (cyc < sweep) sheen(ctx, cx, cy, r, cyc / sweep, Math.PI / 4, r * 0.3, 0.22);
  ctx.restore();
}

// A power-up glyph for UI selectors, drawn in its native colour (the white glyph, same
// as it looks on the spawned pickup), no coin/circle. The selector BUTTON already
// carries the power-up styling (coloured border + tint), so the icon just needs to be
// big. `r` is the glyph half-size in px. `affects` is unused (kept for a stable call site).
export function drawPowerUpGlyph(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, image: any, _affects: string): void {
  glyph(ctx, image, cx, cy, r);
}

// Which token design to draw IN-GAME. Switch with ?pu=1..63 (persists in localStorage);
// default 54 (coin: #48's glitter + slow shine, with a hairline outer rim).
const OK = (s: string | null): boolean => !!s && /^([1-9]|[1-5][0-9]|6[0-3])$/.test(s);
export function getPowerUpVariant(): number {
  return readVariantParam("pu", "puVariant", OK, 54);
}
// Capture ?pu at load so it persists even if navigation strips the URL before a draw.
getPowerUpVariant();
